/**
 * Backend Navigation Utilities
 * 
 * This file contains utilities for navigating between the WordPress frontend
 * and the Laravel backend while maintaining authentication state.
 */

let _ = require('../_.js');
let $ = jQuery;

_.Backend_Navigation = class Backend_Navigation {
    
    /**
     * Navigate to the backend with proper authentication
     * 
     * @param {string} url - The relative URL in the backend to navigate to (without domain)
     * @param {object} options - Additional options for navigation
     * @param {boolean} options.forceLogin - Whether to force login even if already authenticated
     * @returns {Promise<void>}
     */
    static async navigateToBackend(url = '', options = {}) {
        // Default options
        const defaults = {
            forceLogin: false
        };
        
        const settings = { ...defaults, ...options };
        
        try {
            // Check current backend session first
            const sessionResponse = await _.backend_ajax('session', 'check_backend_session');
            const hasBackendSession = sessionResponse?.success && 
                                    sessionResponse?.return?.backend_user;
                
            if (hasBackendSession && !settings.forceLogin) {
                // Already logged in, just navigate
                if (!_.ajax_url_prefix) {
                    throw new Error('[FATAL] _.ajax_url_prefix not set. Backend URL not configured.');
                }
                window.location.href = `${_.ajax_url_prefix}/${url.replace(/^\//, '')}`;
                return;
            }
            
            // Get current token for authentication
            const token = localStorage.getItem('landproz_backend_auth_token');
            
            if (!token && !hasBackendSession) {
                // No token and not logged in - need to show login screen
                alert('Please log in to access the backend system.');
                return;
            }
            
            // We have a token but no session, or we're forcing login
            // Generate an auto-auth URL with the token
            if (!_.ajax_url_prefix) {
                throw new Error('[FATAL] _.ajax_url_prefix not set. Backend URL not configured.');
            }
            const backendUrl = _.ajax_url_prefix;
            
            // Get a one-time auth key from the backend
            const authResponse = await _.backend_ajax('session', 'get_auth_key', {
                _token: token
            });
            
            if (!authResponse?.success || !authResponse?.return?.auth_key) {
                // Failed to get auth key, show login
                alert('Your session has expired. Please log in again.');
                return;
            }
            
            // Navigate to backend with auth key
            const authKey = authResponse.return.auth_key;
            const targetUrl = url ? `/${url.replace(/^\//, '')}` : '';
            window.location.href = `${backendUrl}?auto_auth_key=${authKey}${targetUrl}`;
            
        } catch (error) {
            console.error('Error navigating to backend:', error);
            alert('An error occurred while trying to access the backend. Please try again.');
        }
    }
    
    /**
     * Initialize backend navigation links
     */
    static init() {
        // Initialize click handlers for backend navigation links
        _.on_ui_update_once("[data-ace-target]", function() {
            $(this).click(async function(e) {
                e.preventDefault();
                const target = $(this).data('ace-target') || '';
                await _.Backend_Navigation.navigateToBackend(target);
            });
        });
    }
};